import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Divider,
  Chip,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  CalendarToday as CalendarIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import { toast } from 'react-toastify';
import { generateSalesReport } from '../services/reportService';
import { useTranslation } from 'react-i18next';

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ─── stable module-level helpers ─────────────────────────────────────────────

const _pad = (n) => String(n).padStart(2, '0');
const _ds  = (d) => `${d.getFullYear()}-${_pad(d.getMonth()+1)}-${_pad(d.getDate())}`;

const getInitialFilters = () => {
  const now = new Date();
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  return {
    startDate: `${_ds(lastMonth)}T00:00:00.000`,
    endDate:   `${_ds(now)}T23:59:59.999`,
    documentType: 'all',
    groupBy: 'day',
    paymentMethod: 'all',
    includeVAT: true,
    includeCustomers: true,
    includeProducts: true,
  };
};

const isTicketDoc = (dt) =>
  dt === 'Ticket' || dt === 'ticket_de_caisse' || dt === 'Ticket de Caisse';

const buildReportHTML = (data, flts) => {
  const docTypeLabel =
    flts.documentType === 'all' ? 'All Documents'
    : flts.documentType === 'ticket_de_caisse' ? 'Ticket de Caisse'
    : 'Invoices';
  const paymentLabel = flts.paymentMethod === 'all' ? 'All Methods' : flts.paymentMethod;
  const cc = flts.includeCustomers;
  const genOn = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const vatRows = data.vatBreakdown && Object.keys(data.vatBreakdown).length > 0
    ? Object.entries(data.vatBreakdown).map(([rate, d]) =>
        `<tr>
          <td><span class="badge badge-rate">${rate}%</span></td>
          <td class="right">€${Number(d.subtotal||0).toFixed(2)}</td>
          <td class="right">€${Number(d.vat||0).toFixed(2)}</td>
          <td class="right bold">€${Number(d.total||0).toFixed(2)}</td>
        </tr>`).join('')
    : `<tr><td colspan="4" class="center empty">No VAT data available</td></tr>`;

  const paymentCards = data.paymentMethods && Object.keys(data.paymentMethods).length > 0
    ? Object.entries(data.paymentMethods).map(([method, d]) =>
        `<div class="payment-card">
          <div class="pm-name">${method}</div>
          <div class="pm-amount">€${Number(d.amount||0).toFixed(2)}</div>
          <div class="pm-detail">${d.transactionCount||0} transactions &bull; ${Number(d.percentage||0).toFixed(1)}%</div>
        </div>`).join('')
    : '<p class="empty">No payment data</p>';

  const dueAmountCard = totDue > 0
    ? `<div class="payment-card" style="background:linear-gradient(135deg,#ffebee 0%,#ffcdd2 100%);border-color:#ef9a9a">
          <div class="pm-name" style="color:#c62828">Due Amount</div>
          <div class="pm-amount" style="color:#c62828">€${totDue.toFixed(2)}</div>
          <div class="pm-detail" style="color:#c62828">Outstanding balance</div>
        </div>`
    : '';

  const rows = data.details || [];
  const totSub = rows.reduce((s,r) => s + Number(r.subtotal||0), 0);
  const totVAT = rows.reduce((s,r) => s + Number(r.vat||0), 0);
  const totAmt = rows.reduce((s,r) => s + Number(r.total||0), 0);
  const totDue = rows.reduce((s,r) => s + Number(r.dueAmount||0), 0);

  const detailRows = rows.length > 0
    ? rows.map(s =>
        `<tr>
          <td>${formatDateTime(s.date || s.saleDate)}</td>
          <td>${s.receiptNumber || 'N/A'}</td>
          <td><span class="badge ${isTicketDoc(s.documentType) ? 'badge-ticket' : 'badge-invoice'}">${isTicketDoc(s.documentType) ? 'Ticket' : 'Invoice'}</span></td>
          ${cc ? `<td>${s.customerName || 'Walk-in'}</td>` : ''}
          <td class="right">€${Number(s.subtotal||0).toFixed(2)}</td>
          <td class="right">€${Number(s.vat||0).toFixed(2)}</td>
          <td class="right bold">€${Number(s.total||0).toFixed(2)}</td>
          <td style="vertical-align:top">${
            /^MULTIPLE$/i.test(s.paymentMethod || '') && s.splitPayments && s.splitPayments.length > 0
              ? s.splitPayments.map(sp =>
                  `<div style="margin:1px 0"><span class="badge badge-payment">${sp.PaymentMethodName || sp.PaymentMethod || 'N/A'}&nbsp;€${Number(sp.Amount||0).toFixed(2)}</span></div>`
                ).join('')
              : `<span class="badge badge-payment">${s.paymentMethod || 'N/A'}</span>`
          }</td>
        </tr>`).join('')
    : `<tr><td colspan="${cc ? 8 : 7}" class="center empty">No transactions found for the selected period</td></tr>`;

  const productSection = flts.includeProducts && data.productBreakdown && data.productBreakdown.length > 0
    ? `<div class="section">
        <div class="section-title">Product Sales Breakdown</div>
        <table>
          <thead><tr>
            <th>Product</th><th class="right">Qty Sold</th>
            <th class="right">Total Sales</th><th class="right">Total VAT</th><th class="right">Net Sales</th>
          </tr></thead>
          <tbody>${data.productBreakdown.map(p =>
            `<tr>
              <td>${p.productName || 'N/A'}</td>
              <td class="right">${p.quantitySold || 0}</td>
              <td class="right">€${Number(p.totalSales||0).toFixed(2)}</td>
              <td class="right">€${Number(p.totalVAT||0).toFixed(2)}</td>
              <td class="right bold">€${Number((p.totalSales||0)-(p.totalVAT||0)).toFixed(2)}</td>
            </tr>`).join('')}</tbody>
        </table>
      </div>` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>Sales Report — ${formatDate(flts.startDate)} to ${formatDate(flts.endDate)}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Segoe UI',Arial,sans-serif;font-size:10px;color:#1a1a1a;background:#fff}
    .page{padding:14px 18px;max-width:740px;margin:0 auto}
    /* Header */
    .rh{border-bottom:2px solid #e5e7eb;padding:7px 0 9px;margin-bottom:13px}
    .rh h1{font-size:14px;font-weight:800;color:#111827;margin-bottom:5px;letter-spacing:-.3px}
    .meta{font-size:9px;line-height:1.65;color:#374151}
    /* Section */
    .section{margin-bottom:14px;page-break-inside:avoid}
    .section-title{font-size:9.5px;font-weight:800;color:#374151;text-transform:uppercase;letter-spacing:.7px;padding-bottom:5px;border-bottom:2px solid #e5e7eb;margin-bottom:10px}
    /* Summary — flexible grid that adapts to 4-6 cards */
    .sg{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:6px}
    .sc{padding:9px 6px;border-radius:7px;text-align:center;page-break-inside:avoid}
    .sc .lbl{font-size:7.5px;font-weight:700;text-transform:uppercase;letter-spacing:.3px;opacity:.78;line-height:1.3}
    .sc .val{font-size:15px;font-weight:800;margin-top:3px}
    .sc-g{background:#d1fae5;border:1px solid #6ee7b7}.sc-g .val{color:#065f46}
    .sc-b{background:#dbeafe;border:1px solid #93c5fd}.sc-b .val{color:#1e40af}
    .sc-o{background:#fed7aa;border:1px solid #fdba74}.sc-o .val{color:#b45309}
    .sc-p{background:#e9d5ff;border:1px solid #c084fc}.sc-p .val{color:#6d28d9}
    .sc-t{background:#ccfbf1;border:1px solid #5eead4}.sc-t .val{color:#0f766e}
    .sc-r{background:#fee2e2;border:1px solid #fca5a5}.sc-r .val{color:#b91c1c}
    /* Table */
    table{width:100%;border-collapse:collapse;page-break-inside:auto}
    thead{display:table-header-group}
    tfoot{display:table-footer-group}
    thead tr{background:#1e3a5f}
    thead th{padding:6px 7px;text-align:left;font-size:8px;font-weight:700;color:#fff;text-transform:uppercase;letter-spacing:.4px}
    thead th.right{text-align:right}
    tbody tr:nth-child(even){background:#f8fafc}
    tbody tr{page-break-inside:avoid}
    tbody td{padding:4.5px 7px;font-size:9px;border-bottom:1px solid #eee;vertical-align:middle}
    tbody td.right{text-align:right}
    tbody td.bold{font-weight:700}
    tbody td.center{text-align:center}
    tbody td.empty{color:#9ca3af;padding:14px}
    tfoot td{background:#1e3a5f;color:#fff;font-weight:700;font-size:9px;padding:6px 7px}
    tfoot td.right{text-align:right}
    /* Badges */
    .badge{display:inline-block;padding:2px 6px;border-radius:9px;font-size:8px;font-weight:700;white-space:nowrap}
    .badge-ticket{background:#d1fae5;color:#065f46}
    .badge-invoice{background:#dbeafe;color:#1e40af}
    .badge-payment{background:#e0e7ff;color:#3730a3}
    .badge-rate{background:#fef3c7;color:#92400e;border:1px solid #fcd34d}
    /* Payment grid — flexible layout */
    .pg{display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:6px}
    .payment-card{background:linear-gradient(135deg,#f0f9ff 0%,#e0f2fe 100%);border:1px solid #bae6fd;border-radius:7px;padding:9px 6px;text-align:center;page-break-inside:avoid}
    .pm-name{font-size:7.5px;font-weight:700;color:#0369a1;text-transform:uppercase;letter-spacing:.3px;opacity:.85;line-height:1.3;margin-bottom:3px}
    .pm-amount{font-size:15px;font-weight:800;color:#0f172a}
    .pm-detail{font-size:8px;color:#64748b;margin-top:2px}
    .empty{color:#9ca3af;font-style:italic}
    /* Footer */
    .rfoot{margin-top:16px;padding-top:9px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:8px;color:#9ca3af}
    @page{size:A4 portrait;margin:10mm 12mm}
    @media print{
      body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .page{padding:0;max-width:100%}
      .section{page-break-inside:avoid}
    }
  </style>
</head>
<body>
<div class="page">
  <div class="rh">
    <h1>Sales Report</h1>
    <div class="meta"><strong>Period:</strong>&nbsp;${formatDate(flts.startDate)}&nbsp;&ndash;&nbsp;${formatDate(flts.endDate)}</div>
    <div class="meta"><strong>Generated on:</strong>&nbsp;${genOn}</div>
    <div class="meta"><strong>Document Type:</strong>&nbsp;${docTypeLabel}&nbsp;&nbsp;&bull;&nbsp;&nbsp;<strong>Payment Method:</strong>&nbsp;${paymentLabel}</div>
  </div>

  <div class="section">
    <div class="section-title">Summary</div>
    <div class="sg">
      <div class="sc sc-o"><div class="lbl">Total Sales (Excl. VAT)</div><div class="val">€${Number(data.summary?.totalSalesExclVAT||0).toFixed(2)}</div></div>
      <div class="sc sc-b"><div class="lbl">Total VAT</div><div class="val">€${Number(data.summary?.totalVAT||0).toFixed(2)}</div></div>
      <div class="sc sc-g"><div class="lbl">Total Sales (Incl. VAT)</div><div class="val">€${Number(data.summary?.totalSales||0).toFixed(2)}</div></div>
      <div class="sc sc-p"><div class="lbl">Transactions</div><div class="val">${data.summary?.totalTransactions||0}</div></div>
      <div class="sc sc-t"><div class="lbl">Tickets</div><div class="val">${data.summary?.ticketDeCaisseCount||0}</div></div>
      <div class="sc sc-r"><div class="lbl">Invoices</div><div class="val">${data.summary?.invoiceCount||0}</div></div>
    </div>
  </div>



  <div class="section">
    <div class="section-title">Payment Method Breakdown</div>
    <div class="pg">${paymentCards}${dueAmountCard}</div>
  </div>

  <div class="section">
    <div class="section-title">Sales Details &mdash; ${rows.length} transactions</div>
    <table>
      <thead><tr>
        <th>Date &amp; Time</th>
        <th>Receipt #</th>
        <th>Type</th>
        ${cc ? '<th>Customer</th>' : ''}
        <th class="right">Subtotal</th>
        <th class="right">VAT</th>
        <th class="right">Total</th>
        <th>Payment</th>
      </tr></thead>
      <tbody>${detailRows}</tbody>
      <tfoot><tr>
        <td colspan="${cc ? 4 : 3}">TOTAL &mdash; ${rows.length} transactions</td>
        <td class="right">€${totSub.toFixed(2)}</td>
        <td class="right">€${totVAT.toFixed(2)}</td>
        <td class="right">€${totAmt.toFixed(2)}</td>
        <td></td>
      </tr></tfoot>
    </table>
  </div>

  ${productSection}

  <div class="rfoot">
    <span>Generated by Billeur &bull; Confidential</span>
    <span>${genOn}</span>
  </div>
</div>
</body>
</html>`;
};

// ─────────────────────────────────────────────────────────────────────────────

const SalesReportViewer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [filters, setFilters] = useState(getInitialFilters);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Fetch payment methods on first open
  useEffect(() => {
    if (open && paymentMethodsFromStore.length === 0) {
      dispatch(fetchPaymentMethods());
    }
  }, [open, dispatch, paymentMethodsFromStore.length]);

  // Reset all state every time the dialog opens (fresh start on reopen)
  useEffect(() => {
    if (open) {
      setFilters(getInitialFilters());
      setReportData(null);
      setFiltersExpanded(true);
    }
  }, [open]);

  const handleFilterChange = (field) => (event) => {
    
    let value = event.target.value;

    if (field === 'startDate') {
      value = `${value}T00:00:00.000`;
    } else if (field === 'endDate') {
      value = `${value}T23:59:59.999`;
    }

    setFilters({ ...filters, [field]: value });
  };

  const handleCheckboxChange = (field) => (event) => {
    setFilters({ ...filters, [field]: event.target.checked });
  };

  const handleQuickDate = (range) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    let startDate, endDate;

    switch (range) {
      case 'today':
        startDate = `${dateStr(now)}T00:00:00.000`;
        endDate = `${dateStr(now)}T23:59:59.999`;
        break;
      case 'yesterday': {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        startDate = `${dateStr(y)}T00:00:00.000`;
        endDate = `${dateStr(y)}T23:59:59.999`;
        break;
      }
      case 'week': {
        const w = new Date(now);
        w.setDate(w.getDate() - 7);
        startDate = `${dateStr(w)}T00:00:00.000`;
        endDate = `${dateStr(now)}T23:59:59.999`;
        break;
      }
      case 'month': {
        const m = new Date(now);
        m.setDate(m.getDate() - 30);
        startDate = `${dateStr(m)}T00:00:00.000`;
        endDate = `${dateStr(now)}T23:59:59.999`;
        break;
      }
      case 'quarter': {
        const q = new Date(now);
        q.setDate(q.getDate() - 90);
        startDate = `${dateStr(q)}T00:00:00.000`;
        endDate = `${dateStr(now)}T23:59:59.999`;
        break;
      }
      case 'year': {
        const year = now.getFullYear();
        startDate = `${year}-01-01T00:00:00.000`;
        endDate = `${year}-12-31T23:59:59.999`;
        break;
      }
      default:
        return;
    }

    setFilters({ ...filters, startDate, endDate });
    toast.info(`Date range set to: ${range}`);
  };

  const handleGenerate = async () => {
    if (!filters.startDate || !filters.endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(filters.startDate) > new Date(filters.endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    try {
      setLoading(true);
      const data = await generateSalesReport(filters);
      setReportData(data);
      setFiltersExpanded(false);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handlePDF = useCallback(async () => {
    if (!reportData) return;
    setPdfLoading(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const htmlContent = buildReportHTML(reportData, filters);
      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'position:absolute;left:-9999px;top:0;width:1050px;';
      wrapper.innerHTML = htmlContent;
      document.body.appendChild(wrapper);
      const pageEl = wrapper.querySelector('.page') || wrapper.firstChild;
      await html2pdf().set({
        margin: [8, 8, 8, 8],
        filename: `sales-report-${filters.startDate.slice(0,10)}-to-${filters.endDate.slice(0,10)}.pdf`,
        image: { type: 'jpeg', quality: 0.97 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      }).from(pageEl).save();
      document.body.removeChild(wrapper);
      toast.success(t('pdf_exported_success') || 'PDF exported successfully!');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error(t('pdf_export_failed') || 'Failed to export PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  }, [reportData, filters, t]);

  const handlePrint = useCallback(() => {
    if (!reportData) return;
    const htmlContent = buildReportHTML(reportData, filters);
    const win = window.open('', '_blank', 'width=1080,height=800');
    if (!win) {
      toast.error(t('popup_blocked') || 'Pop-up blocked. Please allow pop-ups for this site.');
      return;
    }
    win.document.write(htmlContent);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  }, [reportData, filters, t]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          📊 Sales Report
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {/* Collapsible Filters Section */}
        <Paper sx={{ mb: 3, overflow: 'hidden' }} elevation={3}>
          <Box
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon />
              <Typography variant="h6" fontWeight={700}>
                Report Filters
              </Typography>
            </Box>
            <IconButton size="small" sx={{ color: 'white' }}>
              {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={filtersExpanded}>
            <Box sx={{ p: 3 }}>
              {/* Quick Date Range Buttons */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Quick Date Range:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    { value: 'today', label: 'Today' },
                    { value: 'yesterday', label: 'Yesterday' },
                    { value: 'week', label: 'Last 7 Days' },
                    { value: 'month', label: 'Last 30 Days' },
                    { value: 'quarter', label: 'Last 90 Days' },
                    { value: 'year', label: 'Last Year' },
                  ].map((range) => (
                    <Chip
                      key={range.value}
                      label={range.label}
                      onClick={() => handleQuickDate(range.value)}
                      color="primary"
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'white',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Date Range */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={filters.startDate.slice(0, 10)}
                    onChange={handleFilterChange('startDate')}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: filters.endDate.slice(0, 10) }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={filters.endDate.slice(0, 10)}
                    onChange={handleFilterChange('endDate')}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: filters.startDate.slice(0, 10),
                      max: new Date().toISOString().slice(0, 10),
                    }}
                  />
                </Grid>
              </Grid>

              {/* Document Type Filter */}
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Document Type</InputLabel>
                  <Select
                    value={filters.documentType}
                    onChange={handleFilterChange('documentType')}
                    label="Document Type"
                    startAdornment={
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        {filters.documentType === 'ticket_de_caisse' ? (
                          <ReceiptIcon sx={{ fontSize: 20 }} />
                        ) : filters.documentType === 'invoice' ? (
                          <DescriptionIcon sx={{ fontSize: 20 }} />
                        ) : null}
                      </Box>
                    }
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>All Documents</Typography>
                        <Chip label="All" size="small" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="ticket_de_caisse">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                        <Typography>Ticket de Caisse</Typography>
                        <Chip label="Receipt" size="small" color="success" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="invoice">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon sx={{ fontSize: 20, color: '#2196f3' }} />
                        <Typography>Invoice (Facture)</Typography>
                        <Chip label="Invoice" size="small" color="primary" />
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {filters.documentType !== 'all' && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                    <Typography variant="caption" color="primary" fontWeight={600}>
                      📄 Filtering by: {filters.documentType === 'ticket_de_caisse' ? 'Ticket de Caisse only' : 'Invoices only'}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Group By & Payment Method */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Group By</InputLabel>
                    <Select
                      value={filters.groupBy}
                      onChange={handleFilterChange('groupBy')}
                      label="Group By"
                    >
                      <MenuItem value="day">Daily</MenuItem>
                      <MenuItem value="week">Weekly</MenuItem>
                      <MenuItem value="month">Monthly</MenuItem>
                      <MenuItem value="quarter">Quarterly</MenuItem>
                      <MenuItem value="year">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={filters.paymentMethod}
                      onChange={handleFilterChange('paymentMethod')}
                      label="Payment Method"
                    >
                      <MenuItem value="all">All Methods</MenuItem>
                      {paymentMethodsFromStore.map((method) => (
                        <MenuItem key={method.id} value={method.name || method.label}>
                          {method.label || method.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Report Options */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Report Options:
                </Typography>
                <FormGroup row sx={{ flexWrap: 'wrap', gap: 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.includeVAT}
                        onChange={handleCheckboxChange('includeVAT')}
                        color="primary"
                      />
                    }
                    label="Include VAT Breakdown"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.includeCustomers}
                        onChange={handleCheckboxChange('includeCustomers')}
                        color="primary"
                      />
                    }
                    label="Include Customer Details"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.includeProducts}
                        onChange={handleCheckboxChange('includeProducts')}
                        color="primary"
                      />
                    }
                    label="Include Product Breakdown"
                  />
                </FormGroup>
              </Box>

              {/* Generate Button */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleGenerate}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  py: 1.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)',
                  },
                }}
              >
                {loading ? 'Generating Report...' : 'Generate Report'}
              </Button>
            </Box>
          </Collapse>
        </Paper>

        {/* Report Results Section */}
        {reportData ? (
          <>
            {/* Report Header */}
            <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Sales Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Period: {reportData.metadata?.reportPeriod || `${formatDate(filters.startDate)} - ${formatDate(filters.endDate)}`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Generated on: {reportData.metadata?.generatedAt ? formatDateTime(reportData.metadata.generatedAt) : formatDateTime(new Date())}
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={`Grouped by: ${filters.groupBy}`}
                  size="small"
                />
                <Chip
                  label={`Payment: ${filters.paymentMethod}`}
                  size="small"
                />
                {filters.documentType !== 'all' && (
                  <Chip
                    label={`Document: ${filters.documentType === 'ticket_de_caisse' ? 'Ticket de Caisse' : 'Invoice'}`}
                    size="small"
                    color="primary"
                  />
                )}
              </Box>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)',
                    color: 'white',
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Total Sales
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    €{Number(reportData.summary?.totalSales || 0).toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
                    color: 'white',
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Total VAT
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    €{Number(reportData.summary?.totalVAT || 0).toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)',
                    color: 'white',
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Transactions
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {reportData.summary?.totalTransactions || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)',
                    color: 'white',
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Avg. Sale
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    €{Number(reportData.summary?.averageSale || 0).toFixed(2)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* VAT Breakdown */}
            {filters.includeVAT && reportData.vatBreakdown && Object.keys(reportData.vatBreakdown).length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  💰 VAT Breakdown
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  * VAT rates are taken from the ticket/invoice line items, not from the product table
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell><strong>VAT Rate</strong></TableCell>
                        <TableCell align="right"><strong>Subtotal (Excl. VAT)</strong></TableCell>
                        <TableCell align="right"><strong>VAT Amount</strong></TableCell>
                        <TableCell align="right"><strong>Total (Incl. VAT)</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(reportData.vatBreakdown).map(([rate, data]) => (
                        <TableRow key={rate} hover>
                          <TableCell>
                            <Chip label={`${rate}%`} size="small" color="primary" />
                          </TableCell>
                          <TableCell align="right">€{Number(data.subtotal || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">€{Number(data.vat || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <strong>€{Number(data.total || 0).toFixed(2)}</strong>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Payment Methods */}
            {reportData.paymentMethods && Object.keys(reportData.paymentMethods).length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  💳 Payment Methods
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(reportData.paymentMethods).map(([method, data]) => (
                    <Grid item xs={6} sm={4} md={3} key={method}>
                      <Card>
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">
                            {method}
                          </Typography>
                          <Typography variant="h5" fontWeight={600}>
                            €{Number(data.amount || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="primary">
                            {data.count || 0} transactions ({Number(data.percentage || 0).toFixed(1)}%)
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  {(() => {
                    const totalDueAmount = (reportData.details || []).reduce(
                      (sum, sale) => sum + Number(sale.dueAmount || 0),
                      0
                    );
                    return totalDueAmount > 0 ? (
                      <Grid item xs={6} sm={4} md={3}>
                        <Card>
                          <CardContent sx={{ bgcolor: '#ffebee' }}>
                            <Typography variant="body2" color="error">
                              Due Amount
                            </Typography>
                            <Typography variant="h5" fontWeight={600} color="error">
                              €{totalDueAmount.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" color="error" sx={{ opacity: 0.8 }}>
                              Outstanding balance
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ) : null;
                  })()}
                </Grid>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Sales Details Table */}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                📋 Sales Details
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Date & Time</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Receipt #</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Type</strong></TableCell>
                      {filters.includeCustomers && (
                        <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Customer</strong></TableCell>
                      )}
                      <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>Subtotal</strong></TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>VAT</strong></TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>Total</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Payment</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.details && reportData.details.length > 0 ? (
                      reportData.details.map((sale, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{formatDateTime(sale.date || sale.saleDate)}</TableCell>
                          <TableCell>
                            <Chip label={sale.receiptNumber || sale.invoiceNumber || 'N/A'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sale.documentType === 'Ticket' || sale.documentType === 'ticket_de_caisse' || sale.documentType === 'Ticket de Caisse' ? 'Ticket' : 'Invoice'}
                              size="small"
                              color={sale.documentType === 'Ticket' || sale.documentType === 'ticket_de_caisse' || sale.documentType === 'Ticket de Caisse' ? 'success' : 'primary'}
                            />
                          </TableCell>
                          {filters.includeCustomers && (
                            <TableCell>{sale.customerName || 'Walk-in'}</TableCell>
                          )}
                          <TableCell align="right">€{Number(sale.subtotal || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">€{Number(sale.vat || sale.vatAmount || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <strong>€{Number(sale.total || sale.totalAmount || 0).toFixed(2)}</strong>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 120, whiteSpace: 'normal' }}>
                            {/^MULTIPLE$/i.test(sale.paymentMethod || '') && sale.splitPayments && sale.splitPayments.length > 0
                              ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                  {sale.splitPayments.map((sp, i) => (
                                    <Chip
                                      key={i}
                                      label={`${sp.PaymentMethodName || sp.PaymentMethod || 'N/A'} €${Number(sp.Amount||0).toFixed(2)}`}
                                      size="small"
                                      color="secondary"
                                      variant="outlined"
                                      sx={{ width: 'fit-content' }}
                                    />
                                  ))}
                                </Box>
                              )
                              : <Chip label={sale.paymentMethod || 'N/A'} size="small" color="primary" />
                            }
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={filters.includeCustomers ? 8 : 7} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No transactions found for the selected period
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Product Breakdown Section */}
            {filters.includeProducts && reportData.productBreakdown && reportData.productBreakdown.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    📦 Product Sales Breakdown
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    * VAT amounts calculated from ticket/invoice line items
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>Product</strong></TableCell>
                          <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>Quantity Sold</strong></TableCell>
                          <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>Total Sales</strong></TableCell>
                          <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>Total VAT</strong></TableCell>
                          <TableCell align="right" sx={{ bgcolor: '#f5f5f5' }}><strong>Net Sales</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.productBreakdown.map((product, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{product.productName || 'N/A'}</TableCell>
                            <TableCell align="right">{product.quantitySold || 0}</TableCell>
                            <TableCell align="right">€{Number(product.totalSales || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">€{Number(product.totalVAT || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">
                              <strong>€{Number((product.totalSales || 0) - (product.totalVAT || 0)).toFixed(2)}</strong>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
          </>
        ) : (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Report Generated Yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select your filters above and click "Generate Report" to view sales data
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t('close') || 'Close'}
        </Button>
        {reportData && (
          <>
            <Button
              onClick={handlePDF}
              disabled={pdfLoading}
              startIcon={pdfLoading ? <CircularProgress size={18} color="inherit" /> : <PdfIcon />}
              variant="outlined"
              color="error"
            >
              {pdfLoading ? (t('exporting') || 'Exporting...') : (t('export_pdf') || 'Export PDF')}
            </Button>
            <Button
              onClick={handlePrint}
              startIcon={<PrintIcon />}
              variant="contained"
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
            >
              {t('print_report') || 'Print Report'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SalesReportViewer;